import Auth from '../auth';
import { saveAs } from 'file-saver';

class Ajax {
  constructor() {
    this.shouldntRefresh = [
      '/authenticate',
      '/authenticate/refresh',
      '/authenticate/token',
      '/authenticate/token/refresh'
    ];
    this.http = window.axios.create({
      withCredentials: true,
      headers: {
        common: {
          Accept: 'application/json'
        }
      }
    });
    this.refreshRequest = null;
  }

  /**
   * Make a get request
   *
   * @param url
   */
  get(url, preloader = true, headers = {}) {
    let loadingKey;

    if (preloader) {
      loadingKey = window.preloaderHandler.signalLoading(url);
    }

    return new Promise((resolve, reject) => {
      this.request('get', url, {}, headers)
        .then(response => {
          window.preloaderHandler.signalComplete(loadingKey);
          resolve(response);
        })
        .catch(error => {
          window.preloaderHandler.signalComplete(loadingKey);
          reject(error);
        });
    });
  }

  /**
   * Make a post request
   *
   * @param url
   * @param data
   * @returns {*|Promise|AxiosPromise}
   */
  post(url, data) {
    return this.request('post', url, data);
  }

  /**
   * Make a put request
   *
   * @param url
   * @param data
   * @returns {*|Promise|AxiosPromise}
   */
  put(url, data) {
    return this.request('put', url, data);
  }

  /**
   * Make a patch request
   *
   * @param url
   * @param data
   * @returns {*|Promise|AxiosPromise}
   */
  patch(url, data = {}) {
    return this.request('patch', url, data);
  }

  /**
   * Make a delete request
   *
   * @param url
   * @param data
   * @returns {*|Promise|AxiosPromise}
   */
  delete(url) {
    return this.request('delete', url);
  }

  /**
   * Make a get request
   *
   * @param url
   */
  download(url, defaultName = 'File.docx') {
    return new Promise((resolve, reject) => {
      return this.request('get', url, {}, {}, 'blob')
        .then(response => {
          let matches = response.headers['content-disposition']?.match(
            /filename=(.*)/
          );

          const name =
            matches && matches.length
              ? matches[1].replaceAll('"', '').replaceAll("'", '')
              : defaultName;

          saveAs(response.data, name);

          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  /**
   * Make the API request, if the request fails because the token has expired
   * attempt to refresh the token and retry the request.
   *
   * @param method
   * @param url
   * @param data
   * @param headers
   * @returns {Promise<unknown>}
   */
  request(method, url, data = {}, headers = {}, responseType = '') {
    if (this.getToken()) {
      headers = Object.assign(headers, {
        Authorization: `Bearer ${this.getToken()}`
      });
    }

    return new Promise((resolve, reject) => {
      this.http({
        method,
        url,
        data,
        headers,
        responseType
      })
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          if (error?.response?.status === 401) {
            // If the user's password has expired log them out and redirect to the password reset screen
            if (error?.response?.data?.error === 'password_expired') {
              window.Token.forget();
              window.location.href = '/reset-expired-password';
              reject(error);
              return;
            }

            // If the user's account is locked redirect them to the login screen
            if (error?.response?.data.error === 'locked') {
              window.Token.forget();
              window.location.href = '/login';
              reject(error);
              return;
            }
          }

          if (
            (error.response?.status === 401 ||
              error.message === 'Network Error') &&
            !this.shouldntRefresh.includes(url)
          ) {
            this.refreshToken()
              .then(() => {
                headers = Object.assign(headers, {
                  Authorization: `Bearer ${this.getToken()}`
                });

                this.http({
                  method: method,
                  url: url,
                  data: data,
                  headers: headers
                })
                  .then(response => {
                    resolve(response);
                  })
                  .catch(error => {
                    reject(error);
                  });
              })
              .catch(error => {
                reject(error);
              });
          } else {
            reject(error);
          }
        });
    });
  }

  /**
   * Refresh the access token.
   *
   * @returns {Promise<unknown>|null}
   */
  refreshToken() {
    if (!this.refreshRequest) {
      const auth = new Auth();

      return (this.refreshRequest = new Promise((resolve, reject) => {
        auth
          .refreshToken()
          .then(() => {
            resolve();
            this.refreshRequest = null;
          })
          .catch(error => {
            reject(error);
            this.refreshRequest = null;
          });
      }));
    }

    return this.refreshRequest;
  }

  /**
   * Get the JWT token.
   *
   * @returns {string}
   */
  getToken() {
    const token = window.Token.get();

    return token;
  }
}

export default Ajax;
